import { router, usePathname } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import {
  View,
  StyleSheet,
  Pressable,
  Animated,
  ScrollView,
  TextInput,
  useWindowDimensions,
  LayoutChangeEvent,
  StyleProp,
  ViewStyle,
} from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StatusBadge } from "./StatusBadge";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import Avatar from "@/components/Avatar";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { useState, useMemo, useRef, useEffect } from "react";

import { ShiftUser } from "@/types/db/shift_users";

interface PositionRosterTabProps {
  positions: ShiftPositionType[];
  shiftId: string;
  style?: StyleProp<ViewStyle>;
}

// User row component
function UserRow({ shiftUser, onPress, colors }: { shiftUser: ShiftUser; onPress: () => void; colors: any }) {
  const user = shiftUser.user;
  const displayName = user
    ? user.preferred_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email_address
    : "Unknown User";

  const getStatusVariant = (status?: string) => {
    switch (status?.toUpperCase()) {
      case "ACCEPTED":
        return "accepted";
      case "DECLINED":
        return "declined";
      case "COMPLETE":
        return "completed";
      case "ATTENDANCE EXCEPTION":
        return "active";
      case "NO SHOW":
        return "declined";
      default:
        return "pending";
    }
  };

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.userItem,
        { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, cursor: "pointer" } as any,
        hovered && { backgroundColor: colors.card },
        pressed && { opacity: 0.7 },
      ]}
    >
      <Avatar userId={user?.id} firstName={user?.first_name} lastName={user?.last_name} size={36} />
      <View style={styles.userInfo}>
        <ThemedText style={styles.userName}>{displayName}</ThemedText>
        {/* {user?.email_address && (
          <ThemedText style={[styles.userEmail, { color: colors.textSecondary }]}>{user.email_address}</ThemedText>
        )} */}
      </View>
      {/* <StatusBadge variant={getStatusVariant(shiftUser.status)} text={shiftUser.status || "PENDING"} size="small" /> */}
      <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
    </Pressable>
  );
}

// Position row component
function PositionRow({ position, onPress, colors }: { position: ShiftPositionType; onPress: () => void; colors: any }) {
  const posName = position.position?.name || "Position";
  const userCount = (position.shift_users || []).filter((su) => su.status?.toUpperCase() === "ACCEPTED").length;
  const headcount = (position as any).headcount || 0;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.positionItem,
        { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, cursor: "pointer" } as any,
        hovered && { backgroundColor: colors.card },
        pressed && { opacity: 0.7 },
      ]}
    >
      <View style={styles.positionIcon}>
        <MaterialCommunityIcons name="badge-account" size={24} color={colors.primary} />
      </View>
      <View style={styles.positionInfo}>
        <ThemedText style={styles.positionName}>{posName}</ThemedText>
        {headcount > 0 && (
          <ThemedText style={[styles.positionMeta, { color: colors.textSecondary }]}>{headcount} needed</ThemedText>
        )}
      </View>
      <View style={[styles.countBadge, { backgroundColor: colors.primary }]}>
        <ThemedText style={styles.countText}>{userCount}</ThemedText>
      </View>
      <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
    </Pressable>
  );
}

export function PositionRosterTab({ positions, shiftId, style }: PositionRosterTabProps) {
  const { colors } = useTheme();
  const pathname = usePathname();
  const { width } = useWindowDimensions();

  const basePath = pathname.includes("/crew/") ? "crew/supervising" : "supervising";

  // State for drill-down
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Height tracking for dynamic resize
  const [positionsHeight, setPositionsHeight] = useState(100);
  const [rosterHeight, setRosterHeight] = useState(100);
  const animatedHeight = useRef(new Animated.Value(160)).current;

  // Animation for horizontal slide
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Filter positions with valid IDs
  const validPositions = useMemo(() => positions.filter((pos) => pos.id), [positions]);

  // Get selected position data
  const selectedPosition = useMemo(() => {
    if (!selectedPositionId) return null;
    return validPositions.find((p) => p.id === selectedPositionId) || null;
  }, [validPositions, selectedPositionId]);

  // All users combined
  const allUsers = useMemo(() => {
    const users: ShiftUser[] = [];
    validPositions.forEach((pos) => {
      (pos.shift_users || []).forEach((su) => {
        users.push(su as ShiftUser);
      });
    });
    return users;
  }, [validPositions]);

  // Filtered users based on search — only show ACCEPTED users
  const filteredUsers = useMemo(() => {
    const users = showAll ? allUsers : ((selectedPosition?.shift_users || []) as ShiftUser[]);
    const accepted = users.filter((su) => su.status?.toUpperCase() === "ACCEPTED");
    if (!searchQuery.trim()) return accepted;
    const q = searchQuery.toLowerCase();
    return accepted.filter((su) => {
      const u = su.user;
      if (!u) return false;
      const name =
        `${u.preferred_name || ""} ${u.first_name || ""} ${u.last_name || ""} ${u.email_address || ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [showAll, allUsers, selectedPosition, searchQuery]);

  // Handle layout measurements
  const handlePositionsLayout = (e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    if (height > 0) setPositionsHeight(height);
  };

  const handleRosterLayout = (e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    if (height > 0) setRosterHeight(height);
  };

  // Animate slide when changing views
  useEffect(() => {
    const toValue = selectedPositionId || showAll ? 1 : 0;
    Animated.spring(slideAnim, {
      toValue,
      useNativeDriver: true,
      tension: 100,
      friction: 12,
    }).start();
  }, [selectedPositionId, showAll, slideAnim]);

  // Animate height when view changes
  useEffect(() => {
    const showingRosterView = selectedPositionId || showAll;
    const targetHeight = showingRosterView ? rosterHeight : positionsHeight;
    // Add header height (~50px)
    Animated.spring(animatedHeight, {
      toValue: targetHeight + 56,
      useNativeDriver: false,
      tension: 100,
      friction: 12,
    }).start();
  }, [selectedPositionId, showAll, positionsHeight, rosterHeight, animatedHeight]);

  // Handle position click
  const handlePositionPress = (positionId: string) => {
    setSearchQuery("");
    setSelectedPositionId(positionId);
  };

  // Handle back button
  const handleBack = () => {
    setSearchQuery("");
    setSelectedPositionId(null);
  };

  // Handle user press - pass actual user ID for navigation
  const handleUserPress = (shiftUser: ShiftUser) => {
    const userId = shiftUser.user?.id;
    if (!userId || !shiftId) return;
    router.navigate(
      basePath.includes("crew")
        ? { pathname: "/crew/supervising/[shift]/user/[user]", params: { shift: shiftId, user: userId } }
        : { pathname: "/supervising/[shift]/user/[user]", params: { shift: shiftId, user: userId } },
    );
  };

  // Handle show all toggle
  const handleShowAllToggle = (value: boolean) => {
    setSearchQuery("");
    setShowAll(value);
    if (value) {
      setSelectedPositionId(null);
    }
  };

  if (!validPositions || validPositions.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.card }]}>
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="account-group" size={48} color={colors.placeholder} />
          <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No positions assigned</ThemedText>
        </View>
      </View>
    );
  }

  // Determine header title
  const headerTitle = showAll
    ? "All Crew"
    : selectedPosition
      ? selectedPosition.position?.name || "Position"
      : "Roster";

  // Check if showing roster view
  const showingRoster = showAll || selectedPositionId;

  // Animation interpolations
  const positionsTranslate = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -width],
  });
  const rosterTranslate = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [width, 0],
  });

  return (
    <Animated.View
      style={[
        styles.container,
        { backgroundColor: colors.card, borderColor: colors.border, height: animatedHeight },
        style,
      ]}
    >
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerLeft}>
          {selectedPosition && !showAll && (
            <Pressable onPress={handleBack} style={styles.backButton}>
              <MaterialCommunityIcons name="chevron-left" size={24} color={colors.primary} />
            </Pressable>
          )}
          <MaterialCommunityIcons
            name={showAll ? "account-group" : selectedPosition ? "badge-account" : "account-group"}
            size={18}
            color={colors.text}
          />
          <ThemedText style={styles.headerTitle}>{headerTitle}</ThemedText>
        </View>
        <View style={styles.toggleSwitch}>
          <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>Show All</ThemedText>
          <ToggleSwitch value={showAll} onValueChange={handleShowAllToggle} />
        </View>
      </View>

      {/* Content with slide animation */}
      <View style={styles.contentWrapper}>
        {/* Positions View */}
        <Animated.View
          onLayout={handlePositionsLayout}
          style={[
            styles.slidePanel,
            { transform: [{ translateX: positionsTranslate }] },
            showingRoster && styles.hidden,
          ]}
        >
          <View style={styles.content}>
            {validPositions.map((position) => (
              <PositionRow
                key={position.id}
                position={position}
                onPress={() => handlePositionPress(position.id!)}
                colors={colors}
              />
            ))}
          </View>
        </Animated.View>

        {/* Roster View */}
        <Animated.View
          onLayout={handleRosterLayout}
          style={[
            styles.slidePanel,
            styles.rosterPanel,
            { transform: [{ translateX: rosterTranslate }] },
            !showingRoster && styles.hidden,
          ]}
        >
          {/* Search */}
          {showingRoster && (
            <View style={styles.searchContainer}>
              <TextInput
                style={[
                  styles.searchInput,
                  { backgroundColor: colors.background, borderColor: colors.border, color: colors.text },
                ]}
                placeholder="Search team members..."
                placeholderTextColor={colors.placeholder}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>
          )}

          {/* Users list with maxHeight */}
          <ScrollView style={styles.rosterScroll} contentContainerStyle={styles.content}>
            {filteredUsers.length === 0 ? (
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="account-group" size={48} color={colors.placeholder} />
                <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>
                  {searchQuery ? "No matching crew" : "No crew assigned"}
                </ThemedText>
              </View>
            ) : (
              filteredUsers.map((shiftUser) => (
                <UserRow
                  key={shiftUser.id}
                  shiftUser={shiftUser}
                  onPress={() => handleUserPress(shiftUser)}
                  colors={colors}
                />
              ))
            )}
          </ScrollView>
        </Animated.View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  backButton: {
    marginLeft: -8,
    marginRight: 4,
  },
  headerTitle: {
    fontWeight: "600",
    fontSize: 15,
  },
  toggleSwitch: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  contentWrapper: {
    position: "relative",
    minHeight: 100,
  },
  slidePanel: {
    width: "100%",
  },
  rosterPanel: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  hidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  content: {
    padding: 12,
    gap: 8,
  },
  searchContainer: {
    paddingHorizontal: 12,
    paddingTop: 12,
  },
  searchInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  rosterScroll: {
    maxHeight: 600,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  // Position row styles
  positionItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  positionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  positionInfo: {
    flex: 1,
  },
  positionName: {
    fontSize: 15,
    fontWeight: "600",
  },
  positionMeta: {
    fontSize: 12,
    marginTop: 2,
  },
  countBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  countText: {
    color: "white",
    fontSize: 12,
    fontWeight: "700",
  },
  // User row styles
  userItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: "500",
  },
  userEmail: {
    fontSize: 12,
    marginTop: 2,
  },
});
